import React, { useEffect, useState } from "react";
import { Bell, LogOut, Menu, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import { getStoredAdmin } from "../utils/adminAuth";

function AdminTopbar({ isMobile = false, isLaptop = false, onMenuClick = () => {} }) {
  const navigate = useNavigate();
  const admin    = getStoredAdmin();
  const [pendingCount, setPendingCount] = useState(0);
  const [showPopup, setShowPopup]       = useState(false);
  const [recent, setRecent]             = useState([]);

  useEffect(() => {
    let active = true;
    const load = () => {
      API.get("/admin/withdraws")
        .then((res) => {
          if (!active) return;
          const all = Array.isArray(res.data) ? res.data : [];
          const pending = all.filter((w) => w.status === "pending");
          setPendingCount(pending.length);
          setRecent(pending.slice(0, 5));
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin-login");
  };

  return (
    <header style={isMobile ? mobileBar : isLaptop ? laptopBar : bar}>
      <div style={left}>
        {isMobile && (
          <button type="button" style={menuBtn} onClick={onMenuClick}>
            <Menu size={18} color="#f97316" />
          </button>
        )}
        <div style={avatarBox}>
          {String(admin?.name || admin?.email || "A").trim().charAt(0).toUpperCase()}
        </div>
        <div style={nameBlock}>
          <span style={nameText}>{admin?.name || admin?.email || "Admin"}</span>
          {!isMobile && (
            <span style={roleText}>{admin?.role === "sub_admin" ? "Sub Admin" : "Super Admin"}</span>
          )}
        </div>
      </div>

      <div style={right}>
        {/* Withdrawal notification bell */}
        <div style={bellWrap}>
          <button
            type="button"
            style={bellBtn}
            onClick={() => setShowPopup((v) => !v)}
            title="Pending withdrawal requests"
          >
            <Bell size={18} color={pendingCount > 0 ? "#f97316" : "#6b7280"} />
            {pendingCount > 0 && (
              <span style={bellBadge}>{pendingCount > 99 ? "99+" : pendingCount}</span>
            )}
          </button>

          {showPopup && (
            <>
              <div style={popupOverlay} onClick={() => setShowPopup(false)} />
              <div style={popup}>
                <div style={popupHead}>
                  <span style={popupTitle}>Pending Withdrawals</span>
                  <span style={popupCount}>{pendingCount}</span>
                </div>
                {recent.length === 0 ? (
                  <div style={popupEmpty}>No pending requests</div>
                ) : (
                  <div style={popupList}>
                    {recent.map((w) => (
                      <div key={w.id} style={popupRow}>
                        <div style={popupRowLeft}>
                          <span style={popupUser}>{w.name || w.phone || `User ${w.user_id}`}</span>
                          <span style={popupPhone}>{w.phone || ""}</span>
                        </div>
                        <span style={popupAmt}>₹{Number(w.amount || 0).toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  style={popupViewAll}
                  onClick={() => { setShowPopup(false); navigate("/admin/payouts"); }}
                >
                  View All Pending Requests →
                </button>
              </div>
            </>
          )}
        </div>

        {!isMobile && (
          <button type="button" style={settingsBtn} onClick={() => navigate("/admin/settings")} title="Settings">
            <Settings size={16} />
            <span>Settings</span>
          </button>
        )}
        <button type="button" style={logoutBtn} onClick={handleLogout} title="Logout">
          <LogOut size={16} />
          {!isMobile && <span>Logout</span>}
        </button>
      </div>
    </header>
  );
}

const base = {
  height: "64px",
  background: "#fff",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 10,
  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
};
const bar       = base;
const laptopBar = { ...base, height: "58px", padding: "0 14px" };
const mobileBar = { ...base, height: "56px", padding: "0 12px" };

const left      = { display: "flex", alignItems: "center", gap: 12 };
const menuBtn   = { width: 36, height: 36, border: "1px solid #e5e7eb", background: "#f8fafc", borderRadius: 10, display: "grid", placeItems: "center", cursor: "pointer" };
const avatarBox = { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #f97316, #ea580c)", color: "#fff", fontWeight: 900, fontSize: 15, display: "grid", placeItems: "center", flexShrink: 0 };
const nameBlock = { display: "flex", flexDirection: "column", gap: 1 };
const nameText  = { color: "#111827", fontSize: 14, fontWeight: 700, lineHeight: 1.2 };
const roleText  = { color: "#6b7280", fontSize: 11, fontWeight: 600 };

const right        = { display: "flex", alignItems: "center", gap: 8, position: "relative" };
const settingsBtn  = { height: 38, padding: "0 14px", border: "1px solid #e5e7eb", background: "#f8fafc", color: "#374151", borderRadius: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const logoutBtn    = { height: 38, padding: "0 14px", border: "1px solid #fecaca", background: "#fff5f5", color: "#dc2626", borderRadius: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" };

// Bell
const bellWrap   = { position: "relative" };
const bellBtn    = {
  width: 38, height: 38, border: "1px solid #e5e7eb", background: "#f8fafc",
  borderRadius: 10, display: "grid", placeItems: "center", cursor: "pointer", position: "relative",
};
const bellBadge  = {
  position: "absolute", top: -6, right: -6,
  background: "#ef4444", color: "#fff",
  borderRadius: 999, minWidth: 18, height: 18,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 900, padding: "0 4px", border: "2px solid #fff",
};

// Popup
const popupOverlay = { position: "fixed", inset: 0, zIndex: 50 };
const popup        = {
  position: "absolute", top: 46, right: 0, zIndex: 60,
  width: 320, background: "#fff", border: "1px solid #e5e7eb",
  borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
  overflow: "hidden",
};
const popupHead    = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
};
const popupTitle   = { fontSize: 14, fontWeight: 800, color: "#111827" };
const popupCount   = {
  background: "#fef3c7", color: "#92400e",
  fontSize: 12, fontWeight: 900, padding: "2px 10px", borderRadius: 999,
};
const popupEmpty   = { padding: "24px 16px", textAlign: "center", color: "#9ca3af", fontSize: 13 };
const popupList    = { display: "flex", flexDirection: "column" };
const popupRow     = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "11px 16px", borderBottom: "1px solid #f9fafb", gap: 8,
};
const popupRowLeft = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 };
const popupUser    = { fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const popupPhone   = { fontSize: 11, color: "#9ca3af" };
const popupAmt     = { fontSize: 14, fontWeight: 900, color: "#059669", flexShrink: 0 };
const popupViewAll = {
  width: "100%", padding: "13px 16px", border: 0, borderTop: "1px solid #e5e7eb",
  background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 13,
  cursor: "pointer", textAlign: "center",
};

export default AdminTopbar;
